import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import domReady from '@wordpress/dom-ready';

gsap.registerPlugin(ScrollTrigger);

const DESKTOP_QUERY = '(min-width: 992px)';

const refreshHandlers = [];

function initTimeline(root) {
  const lineEl = root.querySelector('.timeline-scroll__line');
  const fillEl = root.querySelector('[data-timeline-fill]');
  const steps = Array.from(root.querySelectorAll('[data-timeline-step]'));
  const stepMarkers = steps.map((step) =>
    step.querySelector('.timeline-scroll__step-content')
  );

  if (!lineEl || !fillEl || !stepMarkers[0]) {
    return;
  }

  let thresholds = [];
  let lineHeight = 0;
  let firstStepTop = 0;
  let growableHeight = 1;
  let lastActiveIndex = -1;

  // The fill spans the full line, top to bottom (`grid-row: 1 / -1`, header +
  // steps together), growing from `.timeline-scroll__body`'s own start. The
  // header segment (down to the first step) is always shown filled — it's a
  // floor on the fill height, never tied to scroll — only the steps portion
  // beyond it is scroll-driven. Thresholds/progress are normalized against
  // that remaining `growableHeight` (not the full `lineHeight`), so the fill
  // starts growing as soon as the scroll-trigger starts instead of only
  // after `progress` catches up to the header's share of the line.
  const computeThresholds = () => {
    lineHeight = lineEl.offsetHeight || 1;
    firstStepTop = stepMarkers[0].offsetTop;
    growableHeight = Math.max(lineHeight - firstStepTop, 1);
    fillEl.style.height = `${firstStepTop}px`;
    thresholds = stepMarkers.map(
      (marker) => (marker.offsetTop - firstStepTop) / growableHeight
    );
  };

  const setActiveIndex = (index) => {
    if (index === lastActiveIndex) {
      return;
    }
    steps.forEach((step, i) => {
      step.classList.toggle('is-active', i <= index);
    });
    lastActiveIndex = index;
  };

  computeThresholds();

  refreshHandlers.push(() => {
    computeThresholds();
    ScrollTrigger.refresh();
  });

  const mm = gsap.matchMedia();

  mm.add(
    {
      desktop: `${DESKTOP_QUERY} and (prefers-reduced-motion: no-preference)`,
      desktopReducedMotion: `${DESKTOP_QUERY} and (prefers-reduced-motion: reduce)`,
    },
    (context) => {
      const { desktop, desktopReducedMotion } = context.conditions;

      lastActiveIndex = -1;
      computeThresholds();

      if (desktop) {
        const trigger = ScrollTrigger.create({
          trigger: root,
          // clamp() keeps these positions within the actual scrollable page
          // bounds — without it, `end` can fall past the document's real
          // bottom (e.g. when the block sits near the end of the page), so
          // `progress` never reaches 1 and the fill stalls short of 100%.
          start: 'clamp(top 90%)',
          end: 'clamp(bottom 25%)',
          scrub: true,
          onRefresh: computeThresholds,
          onUpdate: (self) => {
            fillEl.style.height = `${
              firstStepTop + self.progress * growableHeight
            }px`;
            const activeIndex =
              thresholds.filter((threshold) => self.progress >= threshold)
                .length - 1;
            setActiveIndex(activeIndex);
          },
        });

        return () => trigger.kill();
      }

      if (desktopReducedMotion) {
        // No scrub, no scroll math: an instant, non-animated toggle per step.
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const index = stepMarkers.indexOf(entry.target);
              if (entry.isIntersecting && index > lastActiveIndex) {
                setActiveIndex(index);
                fillEl.style.height = `${entry.target.offsetTop}px`;
              }
            });
          },
          { rootMargin: '0px 0px -50% 0px', threshold: 0 }
        );

        stepMarkers.forEach((marker) => observer.observe(marker));

        return () => observer.disconnect();
      }

      return undefined;
    },
    root
  );
}

domReady(() => {
  document
    .querySelectorAll('[data-timeline-scroll]')
    .forEach((root) => initTimeline(root));

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => {
      refreshHandlers.forEach((refresh) => refresh());
    });
  }
});
